import FilterOption from './FilterOption'

export default class Filter {
  static empty () {
    return new Filter({
      hasAnyPreselectedFilterWithZeroMatches: false,
      options: []
    })
  }

  static availableOptionsFrom (availableStays, preselectedFilters) {
    const points = preselectedFilters[FilterOption.POINTS]
      ? FilterOption.points({ order: 1 }).select()
      : FilterOption.points({ order: 1 })
    const freeCancellation = preselectedFilters[FilterOption.FREE_CANCELLATION]
      ? FilterOption.freeCancellation({ order: 2 }).select()
      : FilterOption.freeCancellation({ order: 2 })
    const breakfast = preselectedFilters[FilterOption.BREAKFAST]
      ? FilterOption.breakfast({ order: 3 }).select()
      : FilterOption.breakfast({ order: 3 })
    const packages = preselectedFilters[FilterOption.PACKAGES]
      ? FilterOption.packages({ order: 4 }).select()
      : FilterOption.packages({ order: 4 })
    const accessibleRooms = preselectedFilters[FilterOption.ACCESSIBLE_ROOMS]
      ? FilterOption.accessibleRooms({ order: 5 }).select()
      : FilterOption.accessibleRooms({ order: 5 })
    const optionalOptions = [points, freeCancellation, breakfast, packages]

    const availableOptions = optionalOptions.reduce((accumulation, option) =>
      availableStays.filter(option.match).length > 0
        ? [...accumulation, option]
        : accumulation,
    [])

    const hasAnyPreselectedFilterWithZeroMatches = optionalOptions
      .filter(option => option.selected)
      .reduce((accumulation, option) => accumulation || availableStays.filter(option.match).length === 0, false)

    return new Filter({
      hasAnyPreselectedFilterWithZeroMatches,
      options: [
        ...availableOptions,
        accessibleRooms
      ]
    })
  }

  constructor ({ hasAnyPreselectedFilterWithZeroMatches, options }) {
    this.hasAnyPreselectedFilterWithZeroMatches = hasAnyPreselectedFilterWithZeroMatches
    this.options = options || []
  }

  clone () {
    return new Filter({
      ...this
    })
  }

  hasAnySelection () {
    return this.options
      .filter(option => option.selected)
      .length > 0
  }

  recalculateCounters (matches) {
    const options = this.options
      .map(option => option.updateCounter(matches[option.code] || 0))

    return new Filter({ ...this, options })
  }

  count (stay) {
    return this.options
      .reduce((accumulation, option) => {
        accumulation[option.code] = option.match(stay) ? 1 : 0
        return accumulation
      }, {})
  }

  match (stay) {
    return this.options
      .filter(option => option.selected)
      .reduce((accumulation, option) => {
        const match = option.match(stay)
        accumulation.fullMatch = accumulation.fullMatch && match
        accumulation.matches += match ? 1 : 0
        return accumulation
      }, { stay, fullMatch: true, matches: 0 })
  }

  select (option) {
    return new Filter({
      ...this,
      options: this.options
        .filter(o => o.code !== option.code)
        .concat([option.select()])
        .sort((a, b) => a.order - b.order)
    })
  }

  unselect (option) {
    return new Filter({
      ...this,
      options: this.options
        .filter(o => o.code !== option.code)
        .concat([option.unselect()])
        .sort((a, b) => a.order - b.order)
    })
  }

  unselectPoints () {
    const option = this.options.find(o => o.code === FilterOption.POINTS)
    return option
      ? this.unselect(option)
      : this
  }

  reset () {
    return new Filter({
      ...this,
      options: this.options
        .map(option => option.unselect())
    })
  }

  selection () {
    return {
      [FilterOption.POINTS]: this.options.filter(o => o.code === FilterOption.POINTS && o.selected).length === 1,
      [FilterOption.FREE_CANCELLATION]: this.options.filter(o => o.code === FilterOption.FREE_CANCELLATION && o.selected).length === 1,
      [FilterOption.BREAKFAST]: this.options.filter(o => o.code === FilterOption.BREAKFAST && o.selected).length === 1,
      [FilterOption.PACKAGES]: this.options.filter(o => o.code === FilterOption.PACKAGES && o.selected).length === 1,
      [FilterOption.ACCESSIBLE_ROOMS]: this.options.filter(o => o.code === FilterOption.ACCESSIBLE_ROOMS && o.selected).length === 1
    }
  }
}
